import { OrgStatus } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import {
  LandingPageKycStatus,
  MixpanelEventV2,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { DOCTYPE } from '@features/admin/constants';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem
} from '@utils/storage';

type OrgForLandingKyc = {
  id?: string;
  status?: string;
  document_status?: string;
  role_name?: string;
  access_mode?: string;
};

export const mapOrgToLandingKycStatus = (
  org: OrgForLandingKyc
): LandingPageKycStatus => {
  if (org.document_status === DOCTYPE.RESUBMITTED) {
    return 'resubmitted_pending_kyc';
  }

  switch (org.status) {
    case OrgStatus.REJECTED:
      return 'rejected';
    case OrgStatus.APPROVED:
      return org.access_mode === 'read_only' ? 'active_read_only' : 'active';
    case OrgStatus.PENDING:
    case OrgStatus.VERIFICATION_SUBMITTED:
      return 'pending_verification';
    case OrgStatus.VERIFICATION_PENDING:
      return 'active_pending_kyc';
    default:
      return 'active';
  }
};

const getLandingKycStatusByOrg = () => {
  const stored = getLocalStorageItem(STORAGE_KEYS.LANDING_PAGE_KYC_STATUS);
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return {};
  }
  return stored;
};

type TrackLandingPageViewedParams = {
  org: OrgForLandingKyc | null | undefined;
  profileName?: string;
  userRole?: string;
  mobileNumber?: string;
  orgCount: number;
};

/**
 * Fires "Landing Page After Login Viewed" when mapped kyc_status for the
 * current org differs from the last value stored for that orgId.
 * Shape in localStorage: { [orgId]: LandingPageKycStatus }.
 * Cleared on logout with the rest of localStorage.
 */
export const trackLandingPageViewedIfNeeded = ({
  org,
  profileName,
  userRole,
  mobileNumber,
  orgCount
}: TrackLandingPageViewedParams) => {
  const orgId = org?.id;
  if (!orgId || !org?.status) return;

  const kycStatus = mapOrgToLandingKycStatus(org);
  const statusByOrg = getLandingKycStatusByOrg();
  const storedStatus = statusByOrg[orgId];

  if (storedStatus === kycStatus) return;

  const isFirstTimeFlag = getLocalStorageItem(STORAGE_KEYS.IS_FIRST_TIME_LOGIN);
  const isFirstTimeLogin = isFirstTimeFlag === true && orgCount === 1;

  if (isFirstTimeFlag) {
    removeLocalStorageItem(STORAGE_KEYS.IS_FIRST_TIME_LOGIN);
  }

  setLocalStorageItem(STORAGE_KEYS.LANDING_PAGE_KYC_STATUS, {
    ...statusByOrg,
    [orgId]: kycStatus
  });

  sendMixpanelEventV2(MixpanelEventV2.LANDING_PAGE_AFTER_LOGIN_VIEWED, {
    mobile_number:
      mobileNumber || getLocalStorageItem(STORAGE_KEYS.MOBILE) || '',
    country_code: '91',
    profile_name: profileName || '',
    kyc_status: kycStatus,
    is_first_time_login: isFirstTimeLogin ? 'yes' : 'no',
    user_role: userRole || org.role_name || ''
  });
};
